<template>
  <section class="soft-skills-dynamics">
    <div class="soft-skills-dynamics__header">
      <h2 class="soft-skills-dynamics__title">
        Динамика роста гибких навыков
      </h2>

      <div class="soft-skills-dynamics__filters">
        <a-select
            v-model:value="selectedGroup"
            :options="groupOptions"
            :disabled="!hasSubscription"
            class="soft-skills-dynamics__select"
        />

        <div>
          <a-date-picker
              v-model:value="selectedPeriod"
              :disabled="!hasSubscription"
              picker="month"
              format="MMMM YYYY"
              value-format="YYYY-MM"
              inputReadOnly
              :allowClear="false"
              class="soft-skills-dynamics__date compact-month-picker"
          />
        </div>
      </div>
    </div>

    <div class="soft-skills-dynamics__grid">
      <SmallCardWrapper
          name="Динамика роста гибких навыков"
          :helpText="'Динамика роста гибких навыков'"
          :loading="dataIsLoading"
          :locked="!hasSubscription"
          :showLockedContent="true"
      >
        <div v-if="!hasSubscription" class="subsription-locked">
          <SubscriptionRequiredWarning
              @showOrder="showOrderForm"
              :customText="'Отслеживайте динамику роста гибких навыков в компании за разный период времени и находите проблемные места.'" />
        </div>

        <SoftSkillsGrowthCard
            :score="growth.score"
            :change="growth.change"
            :changeLabel="growth.changeLabel"
            :indexes="growth.indexes"
            :chartLabels="growth.chartLabels"
            :chartValues="growth.chartValues"
            :class="{ locked: !hasSubscription }"
        />
      </SmallCardWrapper>

      <SmallCardWrapper
          name="Динамика утверждений"
          :helpText="'Динамика утверждений'"
          :loading="dataIsLoading"
          :locked="!hasSubscription"
          :showLockedContent="true"
      >
        <div v-if="!hasSubscription" class="subsription-locked">
          <SubscriptionRequiredWarning
              @showOrder="showOrderForm"
              :customText="'Отслеживайте динамику динамики утверждений в компании за разный период времени и находите проблемные места.'" />
        </div>

        <AssertionsDynamicsCard
            :items="assertions"
            :class="{ locked: !hasSubscription }"
        />
      </SmallCardWrapper>
    </div>
  </section>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import dayjs from 'dayjs'

import SmallCardWrapper from '@/components/Widgets/WidgetCard.vue'
import AssertionsDynamicsCard from "@/components/Analytics/AssertionsDynamicsCard.vue"
import SoftSkillsGrowthCard from "@/components/Analytics/SoftSkillsGrowthCard.vue"
import SubscriptionRequiredWarning from "@/components/OtherElements/SubscriptionRequiredWarning.vue"

export default {
  name: 'SoftSkillsDynamics',

  props: {
    hasSubscription: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    SubscriptionRequiredWarning,
    SoftSkillsGrowthCard,
    AssertionsDynamicsCard,
    SmallCardWrapper,
  },

  setup(props, context) {
    const store = useStore()
    const groups = computed(() => store.state.group.groups);

    const selectedGroup = ref('all');
    const selectedPeriod = ref(dayjs().format('YYYY-MM'));
    const dataIsLoading = ref(false);

    const hasSubscriptionAccess = computed(() => props.hasSubscription);

    const groupOptions = computed(() => {
      return [
        {
          label: 'Все сотрудники',
          value: 'all',
        },
        ...groups.value.map((group) => ({
          label: group.name,
          value: group.id,
        })),
      ]
    })

    const softSkillsDynamicsData = computed(() => {
      return store.state.analytics.monitoringData?.softSkillsDynamics ?? null
    })

    const DEFAULT_GROWTH = {
      score: 85.5,
      change: 21,
      changeLabel: 'за период',
      indexes: [
        {
          label: 'Индекс вырос',
          value: 10,
        },
        {
          label: 'Индекс на том же уровне',
          value: 32,
        },
        {
          label: 'Индекс снизился',
          value: 3,
        },
      ],
      chartLabels: ['Окт', 'Ноя', 'Дек', 'Янв', 'Фев', 'Мар'],
      chartValues: [0, -15, 8, -18, -4, 34],
    }

    const DEFAULT_ASSERTIONS = [
      {
        text: 'Управляю своими эмоциями в сложных ситуациях',
        change: 9,
        users: 10,
      },
      {
        text: 'Лучше справляюсь со сложными ситуациями',
        change: -3,
        users: 32,
      },
      {
        text: 'Общаюсь с коллегами более конструктивно',
        change: 2,
        users: 45,
      },
      {
        text: 'Решаю конфликты и недопонимания быстрее',
        change: 25,
        users: 65,
      },
      {
        text: 'Планирую работу с учетом ресурсов команды',
        change: 10,
        users: 108,
      },
      {
        text: 'Увеличилась эффективность совещаний',
        change: -17,
        users: 88,
      },
      {
        text: 'Решения принимаем быстрее и слаженнее',
        change: -7,
        users: 5,
      },
    ]

    const growth = ref({
      score: 0,
      change: 0,
      changeLabel: 'за период',
      indexes: [],
      chartLabels: [],
      chartValues: [],
    });

    const assertions = ref([])

    const getSafeNumber = (value) => {
      const numericValue = Number(value)
      return Number.isFinite(numericValue) ? numericValue : 0
    }

    const getSignedChange = (value, direction) => {
      const numericValue = getSafeNumber(value)

      if (direction === 'down') {
        return -Math.abs(numericValue)
      }

      if (direction === 'up') {
        return Math.abs(numericValue)
      }

      return 0
    }

    const periodInterval = computed(() => {
      if (!selectedPeriod.value) {
        return null
      }

      const monthDate = dayjs(`${selectedPeriod.value}-01`)

      return [
        monthDate.startOf('month').format('YYYY.MM.DD'),
        monthDate.endOf('month').format('YYYY.MM.DD'),
      ].join('-')
    })

    const buildGrowthFromBackend = (data) => {
      const growthScore = data?.growth_score || {}
      const chart = growthScore?.chart || {}
      const chartLabels = Array.isArray(chart?.labels) ? chart.labels : []
      const chartChanges = Array.isArray(chart?.percent_change)
          ? chart.percent_change
          : []
      const chartDirections = Array.isArray(chart?.percent_change_direction)
          ? chart.percent_change_direction
          : []
      const chartItemsCount = Math.min(chartLabels.length, chartChanges.length)

      return {
        score: getSafeNumber(growthScore.percent),
        change: getSignedChange(
            growthScore.percent_change,
            growthScore.percent_change_direction
        ),
        changeLabel: 'за период',
        indexes: [
          {
            label: 'Индекс вырос',
            value: getSafeNumber(growthScore?.dynamics?.higher),
          },
          {
            label: 'Индекс на том же уровне',
            value: getSafeNumber(growthScore?.dynamics?.same),
          },
          {
            label: 'Индекс снизился',
            value: getSafeNumber(growthScore?.dynamics?.lower),
          },
        ],
        chartLabels: chartLabels.slice(0, chartItemsCount),
        chartValues: chartChanges
            .slice(0, chartItemsCount)
            .map((value, index) => getSignedChange(
                value,
                chartDirections[index]
            )),
      }
    }

    const buildAssertionsFromBackend = (data) => {
      const backendItems = Array.isArray(data?.statements_dynamics)
          ? data.statements_dynamics
          : []

      return backendItems.map((item) => ({
        text: item?.statement_name || '',
        change: getSignedChange(item?.dynamics_percent, item?.dynamics),
        users: getSafeNumber(item?.count),
      }))
    }

    const fillCardsData = (backendData) => {
      if (!hasSubscriptionAccess.value) {
        growth.value = {
          ...DEFAULT_GROWTH,
          indexes: DEFAULT_GROWTH.indexes.map((item) => ({ ...item })),
          chartLabels: [...DEFAULT_GROWTH.chartLabels],
          chartValues: [...DEFAULT_GROWTH.chartValues],
        }

        assertions.value = DEFAULT_ASSERTIONS.map((item) => ({ ...item }))
        return
      }

      growth.value = buildGrowthFromBackend(backendData)
      assertions.value = buildAssertionsFromBackend(backendData)
    }

    const fetchData = () => {
      if (!hasSubscriptionAccess.value) {
        fillCardsData(null)
        return
      }

      dataIsLoading.value = true;

      store.dispatch('analytics/getSoftSkillsDynamics', {
        group_id: selectedGroup.value === 'all' ? null : selectedGroup.value,
        date: periodInterval.value,
      }).then(() => {
        fillCardsData(softSkillsDynamicsData.value)
      }).finally(() => {
        dataIsLoading.value = false
      })
    }

    const showOrderForm = () => {
      context.emit('showOrderForm');
    }

    watch(
        () => [softSkillsDynamicsData.value, hasSubscriptionAccess.value],
        () => {
          fillCardsData(softSkillsDynamicsData.value)
        },
        { immediate: true }
    );

    watch(
        () => [selectedGroup.value, periodInterval.value],
        () => {
          fetchData()
        }
    );

    return {
      selectedGroup,
      selectedPeriod,
      groupOptions,
      growth,
      assertions,
      dataIsLoading,
      showOrderForm
    }
  },
}
</script>

<style scoped lang="scss">
.soft-skills-dynamics {
  width: 100%;
  text-align: start;
}

.soft-skills-dynamics__header {
  margin-bottom: 20px;
}

.soft-skills-dynamics__title {
  margin: 0 0 20px;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);

  @media (max-width: $mobile-breakpoint) {
    padding: 0 20px;
  }
}

.soft-skills-dynamics__filters {
  display: flex;
  align-items: center;
  gap: 12px;
}

.soft-skills-dynamics__select {
  width: 350px;
}

.soft-skills-dynamics__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
}

.soft-skills-dynamics__grid :deep(.ant-card-body) {
  padding: 0;
}

.soft-skills-dynamics__grid :deep(.small-card-wrapper__body) {
  align-items: stretch;
}

.soft-skills-dynamics__date {
  width: 200px;
}

@media (max-width: $tablet-breakpoint) {
  .soft-skills-dynamics__grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: $mobile-breakpoint) {
  .soft-skills-dynamics__filters {
    padding: 0 20px;
  }

  .soft-skills-dynamics__select,
  .soft-skills-dynamics__date {
    width: 100%;
  }
}

.compact-month-picker {
  display: flex;
  align-items: center;
}

:deep(.compact-month-picker .ant-picker-input) {
  flex: 0 1 auto;
}

:deep(.compact-month-picker .ant-picker-input > input) {
  padding: 0;
  font-size: 14px;
  text-align: center;
}

:deep(.compact-month-picker .ant-picker-clear) {
  display: none;
}

:deep(.compact-month-picker .ant-picker-suffix) {
  margin-left: 6px;
  color: rgba(0, 0, 0, 0.45);
}

:deep(.compact-month-picker .ant-picker-input-placeholder > input) {
  text-align: center;
}

.locked {
  opacity: 0.5;
}

.subsription-locked {
  margin-bottom: 16px;
}
</style>
